import {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react";

import { Box } from "@mui/material";

import { DateTime } from "luxon";

import { DataSet } from "vis-data";
import {
    Timeline,
    TimelineEventPropertiesResult,
    TimelineItem,
    TimelineGroup,
    DataItemCollectionType,
} from "vis-timeline/standalone";

import { useAppSelector } from "app/hooks";

import { PlaybackTimelineGroup } from "./PlaybackTimelineGroup";
import { PlaybackTimelineMarker } from "./PlaybackTimelineMarker";
import { playbackSelectors } from "./store/selectors.playback";

import "./style/TimelineStyling.scss";

export interface TimelineProps {
    startDateTime: string;
    endDateTime: string;
    onSetPlaybackDateTime: (dateTime: DateTime) => void;
    onTimelineGroupsChanged: () => void;
}

export interface PlaybackTimelineRef {
    redrawTimeline: () => void;
}

export const PlaybackTimeline = forwardRef<PlaybackTimelineRef, TimelineProps>(
    (props: TimelineProps, ref: ForwardedRef<PlaybackTimelineRef>) => {
        useImperativeHandle(ref, () => {
            return {
                redrawTimeline() {
                    if (timelineInstance.current) {
                        timelineInstance.current.redraw();
                    }
                },
            };
        }, []);

        const selectedVisibleDossierDeviceIds = useAppSelector(
            playbackSelectors.selectVisibleDossierDeviceIds,
        );

        // Timeline
        const onTimeChanged = useCallback(
            (eventProps: TimelineEventPropertiesResult) => {
                props.onSetPlaybackDateTime(
                    DateTime.fromJSDate(eventProps.time),
                );
            },
            [props],
        );

        const timelineContainerRef = useRef<HTMLElement>(undefined);
        const timelineInstance = useRef<Timeline>(undefined);
        const timelineItems = useRef<DataSet<TimelineItem, "id">>(
            new DataSet([]),
        );

        const timelineGroups = useRef<DataSet<TimelineGroup, "id">>(
            new DataSet([]),
        );

        if (!timelineInstance.current && timelineContainerRef.current) {
            const startDate = new Date(props.startDateTime);
            const endDate = new Date(props.endDateTime);
            const timeSpanOfTimeLine = endDate.getTime() - startDate.getTime();
            timelineInstance.current = new Timeline(
                timelineContainerRef.current,
                timelineItems.current as DataItemCollectionType,
                timelineGroups.current,
                {
                    stack: false,
                    maxHeight: 200,
                    horizontalScroll: false,
                    verticalScroll: true,
                    start: startDate,
                    end: endDate,
                    orientation: {
                        axis: "top",
                        item: "top",
                    },
                    margin: {
                        item: 0,
                    },
                    showCurrentTime: true,
                    showMajorLabels: false,
                    showMinorLabels: true,
                    groupHeightMode: "fixed",
                    zoomMin: 180000, // = 3 minutes, the minimum zoom allowed
                    zoomMax: timeSpanOfTimeLine,
                },
            );

            timelineInstance.current.on("timechanged", onTimeChanged);
            timelineInstance.current.on("doubleClick", onTimeChanged);
            timelineGroups.current.on("*", props.onTimelineGroupsChanged);
        }

        useEffect(() => {
            if (timelineInstance.current) {
                const startDateTime = new Date(props.startDateTime);
                const endDateTime = new Date(props.endDateTime);
                const timeSpanOfTimeLine =
                    endDateTime.getTime() - startDateTime.getTime();

                timelineInstance.current.setOptions({
                    start: startDateTime,
                    end: endDateTime,
                    zoomMax: timeSpanOfTimeLine,
                });

                timelineInstance.current.fit();
            }
        }, [props.startDateTime, props.endDateTime]);

        return (
            <>
                <Box ref={timelineContainerRef}>
                    {timelineInstance.current && (
                        <PlaybackTimelineMarker
                            timelineInstance={timelineInstance.current}
                        />
                    )}
                    {selectedVisibleDossierDeviceIds.map((dossierDeviceId) => (
                        <PlaybackTimelineGroup
                            key={dossierDeviceId}
                            timelineGroups={timelineGroups}
                            timelineItems={timelineItems}
                            dossierDeviceId={dossierDeviceId}
                            startDateTime={props.startDateTime}
                            endDateTime={props.endDateTime}
                        />
                    ))}
                </Box>
            </>
        );
    },
);
